import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from '../hooks/useForm';
import { useRegister } from '../hooks/useRegister';
import '../auth.css';
import MainHeader from '../header/MainHeader';
import Footer from '../footer/Footer';
import { useGoogleAuth } from '../hooks/useGoogleAuth';

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, loading, error } = useRegister();
  const { startGoogleSignIn } = useGoogleAuth();

  const { values, handleChange, handleSubmit } = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
    },
    onSubmit: async formData => {
      await register(formData);
    },
  });

  return (
    <div className="auth-page-light home-page-light">
      <MainHeader />
      
      <main className="auth-main">
        <div className="auth-visual-bg"></div>
        
        <div className="auth-container">
          <div className="auth-glass-card">
            {/* Form Side */}
            <div className="auth-form-side">
              <div className="auth-nav-tabs">
                <button className="auth-nav-link" onClick={() => navigate('/login')}>{t('register.tab_login')}</button>
                <button className="auth-nav-link active">{t('register.tab_register')}</button>
              </div>

              <h2>{t('register.title')}</h2>
              <p className="auth-subtitle">{t('register.subtitle')}</p>

              <div className="social-btns">
                <button type="button" className="btn-social" onClick={startGoogleSignIn}>
                  <img src="https://w7.pngwing.com/pngs/702/463/png-transparent-favicon-google-logo-new-new-google-logo-icon-thumbnail.png" width="20" alt="Google" />
                  Google
                </button>
                <button type="button" className="btn-social">
                  <img src="https://cdn-icons-png.flaticon.com/512/179/179309.png" width="20" alt="Apple" />
                  Apple
                </button>
              </div>

              <div className="divider-text">{t('register.or_with_email')}</div>

              {error && <div className="alert alert-danger border-0 rounded-3 mb-4">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t('register.first_name')}</label>
                    <input
                      type="text"
                      className="auth-input"
                      placeholder={t('register.first_name_placeholder')}
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t('register.last_name')}</label>
                    <input
                      type="text"
                      className="auth-input"
                      placeholder={t('register.last_name_placeholder')}
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">{t('register.email')}</label>
                  <input
                    type="email"
                    className="auth-input"
                    placeholder={t('register.email_placeholder')}
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">{t('register.phone')}</label>
                  <div className="input-group">
                    <span className="input-group-text">+355</span>
                    <input
                      type="tel"
                      className="auth-input"
                      placeholder={t('register.phone_placeholder')}
                      name="phone"
                      value={values.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label">{t('register.password')}</label>
                  <input
                    type="password"
                    className="auth-input"
                    placeholder={t('register.password_placeholder')}
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    required
                  />
                </div>

                <button type="submit" className="btn-auth-submit" disabled={loading}>
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : null}
                  {loading ? t('register.submitting') : t('register.submit')}
                </button>
              </form>

              <div className="auth-footer-text">
                {t('register.have_account')} <Link to="/login" className="auth-link">{t('register.login_here')}</Link>
                <div className="mt-3 pt-3 border-top" style={{ fontSize: '0.85rem' }}>
                  {t('register.dealer_prompt')} <Link to="/dealer-register" className="auth-link">{t('register.dealer_link')}</Link>
                </div>
              </div>
            </div>

            {/* Info Side */}
            <div className="auth-info-side">
              <div className="info-content">
                <h3 className="info-title">{t('register.info_title')}</h3>

                <div className="advantage-list">
                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-shield-check text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('register.feature_secure_title')}</h4>
                      <p>{t('register.feature_secure_desc')}</p>
                    </div>
                  </div>

                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-lightning-charge text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('register.feature_sell_title')}</h4>
                      <p>{t('register.feature_sell_desc')}</p>
                    </div>
                  </div>

                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-star text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('register.feature_premium_title')}</h4>
                      <p>{t('register.feature_premium_desc')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
